/**
 * Analytics event constants
 * Single source of truth for all event names
 * Use `as const` for type inference
 */
export const ANALYTICS_EVENTS = {
  COMPLIANCE: {
    PAGE_VIEWED: 'Compliance Page Viewed',
    GET_STARTED_CLICKED: 'Compliance Get Started Clicked',
    BRAND_CREATION_STARTED: 'Brand Creation Started',
    BRAND_CREATION_SUBMITTED: 'Brand Creation Submitted',
    BRAND_CREATION_SUCCEEDED: 'Brand Creation Succeeded',
    BRAND_CREATION_FAILED: 'Brand Creation Failed',
    BRAND_DOCUMENT_UPLOADED: 'Brand Document Uploaded',
    BRAND_DOCUMENT_VERIFICATION_SUCCEEDED: 'Brand Document Verification Succeeded',
    BRAND_DOCUMENT_VERIFICATION_FAILED: 'Brand Document Verification Failed',
    BRAND_OTP_DIALOG_OPENED: 'Brand OTP Dialog Opened',
    BRAND_OTP_DIALOG_DISMISSED: 'Brand OTP Dialog Dismissed',
    BRAND_OTP_VERIFICATION_SUBMITTED: 'Brand OTP Verification Submitted',
    BRAND_OTP_VERIFICATION_SUCCEEDED: 'Brand OTP Verification Succeeded',
    BRAND_OTP_VERIFICATION_FAILED: 'Brand OTP Verification Failed',
    BRAND_OTP_RESEND_REQUESTED: 'Brand OTP Resend Requested',
    BRAND_OTP_RESEND_FAILED: 'Brand OTP Resend Failed',
    CAMPAIGN_CREATION_STARTED: 'Campaign Creation Started',
    CAMPAIGN_CREATION_SUBMITTED: 'Campaign Creation Submitted',
    CAMPAIGN_CREATION_SUCCEEDED: 'Campaign Creation Succeeded',
    CAMPAIGN_CREATION_FAILED: 'Campaign Creation Failed',
    CAMPAIGN_CREATION_REVIEW_FAILED: 'Campaign Creation Review Failed',
    AI_REVIEW_SUGGESTION_REPORTED: 'AI Review Suggestion Reported',
    CAMPAIGN_REVIEW_ISSUE_RESOLVED: 'Campaign Review Issue Resolved',
    CAMPAIGN_REVIEW_ISSUE_UNRESOLVED: 'Campaign Review Issue Unresolved',
    CAMPAIGN_REVIEW_ISSUE_DISMISSED: 'Campaign Review Issue Dismissed',
    CAMPAIGN_REVIEW_SKIP_OPENED: 'Campaign Review Skip Opened',
    CAMPAIGN_REVIEW_SKIP_CONFIRMED: 'Campaign Review Skip Confirmed',
    CAMPAIGN_REVIEW_SKIP_CANCELLED: 'Campaign Review Skip Cancelled',
  },
  COMPANIES: {
    TAB_OPENED: 'Companies Tab Opened',
    VIEWED: 'Company Viewed',
    CREATED: 'Company Created',
    UPDATED: 'Company Updated',
    CREATION_STARTED: 'Company Creation Started',
    FILTER_APPLIED: 'Company Filter Applied',
    FILTER_CLEARED: 'Company Filter Cleared',
    SORT_CHANGED: 'Company Sort Changed',
    TEAM_MEMBER_ADDED: 'Company Team Member Added',
    TEAM_MEMBER_REMOVED: 'Company Team Member Removed',
  },
  FILTERS: {
    APPLIED: 'Filter Applied',
    REMOVED: 'Filter Removed',
    CLEARED: 'Filters Cleared',
    SORTING_APPLIED: 'Sorting Applied',
  },
  COMMON: {
    SIGNED_IN: 'Signed In',
    SIGNED_OUT: 'Signed Out',
  },
  APP: {
    /** The "We can't reach our servers" splash rendered in error mode. */
    SPLASH_ERROR_SHOWN: 'App Splash Error Shown',
  },
  GLOBAL_SEARCH: {
    OPENED: 'Global Search Opened',
    CLOSED: 'Global Search Closed',
    RESULT_SELECTED: 'Global Search Result Selected',
    RESULT_CALLED: 'Global Search Result Called',
    FILTER_CHANGED: 'Global Search Filter Changed',
    NO_RESULTS_ACTION: 'Global Search No Results Action',
  },
  VOIP: {
    REGION_CONNECTED: 'Vonage Region Connected',
  },
  ONBOARDING: {
    GUIDE_STARTED: 'Onboarding Guide Started',
    GUIDE_STEP_VIEWED: 'Onboarding Guide Step Viewed',
    GUIDE_COMPLETED: 'Onboarding Guide Completed',
    GUIDE_DISMISSED: 'Onboarding Guide Dismissed',
    GUIDE_STEP_SKIPPED: 'Onboarding Guide Step Skipped',
  },
  CALL_QUALITY: {
    BANNER_SHOWN: 'Call Quality Banner Shown',
    PANEL_OPENED: 'Call Quality Panel Opened',
  },
  MIC_SILENCE: {
    DETECTED: 'Mic Silence Detected',
    RESOLVED: 'Mic Silence Resolved',
    BANNER_CLICKED: 'Mic Silence Banner Clicked',
  },
  COLUMNS_TOGGLER: {
    OPENED: 'Columns Toggler Opened',
    COLUMN_TOGGLED: 'Column Toggled',
  },
  BLOCKED_USER: {
    SCREEN_VIEWED: 'Blocked User Screen Viewed',
  },
  INBOX: {
    QUICK_FILTER_CHANGED: 'Inbox Quick Filter Changed',
    TYPE_FILTER_APPLIED: 'Inbox Type Filter Applied',
    TEAM_FILTER_APPLIED: 'Inbox Team Filter Applied',
  },
  SUMMARIES: {
    TAB_OPENED: 'Summaries Tab Opened',
    VIEWED: 'Summary Viewed',
    TIME_SPENT: 'Summary Time Spent',
    TAB_SWITCHED: 'Summary Tab Switched',
    ACTIONS_SHORTCUT_CLICKED: 'Summary Actions Shortcut Clicked',
    CONTENT_COPIED: 'Summary Content Copied',
    SHARE_STARTED: 'Summary Share Started',
    SHARE_LINK_COPIED: 'Summary Share Link Copied',
    SHARE_REVOKED: 'Summary Share Revoked',
    SHARE_POPOVER_OPENED: 'Summary Share Popover Opened',
    SHARE_PUBLISH_TOGGLED: 'Summary Share Publish Toggled',
    SHARE_INVITE_SCREEN_OPENED: 'Summary Share Invite Screen Opened',
    SHARE_EMAIL_INVITE_SENT: 'Summary Share Email Invite Sent',
    SHARE_MEMBER_ADDED: 'Summary Share Member Added',
    SHARE_MEMBER_REMOVED: 'Summary Share Member Removed',
    SHARE_WORKSPACE_TOGGLED: 'Summary Share Workspace Toggled',
    RECORDING_PLAYED: 'Summary Recording Played',
    RECORDING_DOWNLOADED: 'Summary Recording Downloaded',
    PLAYBACK_SPEED_CHANGED: 'Summary Playback Speed Changed',
    TRANSCRIPT_ROW_CLICKED: 'Summary Transcript Row Clicked',
    NO_TRANSCRIPT: 'Summary No Transcript',
    FILTER_APPLIED: 'Summaries Filter Applied',
    FILTER_CLEARED: 'Summaries Filter Cleared',
    NEXT_PAGE_LOADED: 'Summaries Next Page Loaded',
    RECENTS_CARD_CLICKED: 'Summary Recents Card Clicked',
    EMPTY_STATE_SHOWN: 'Summary Empty State Shown',
    PUBLIC_PAGE_VIEWED: 'Public Summary Page Viewed',
    PUBLIC_CTA_CLICKED: 'Public Summary CTA Clicked',
    PUBLIC_NOT_FOUND: 'Public Summary Not Found',
    PUBLIC_TIME_SPENT: 'Public Summary Time Spent',
    PUBLIC_PAGE_SCROLLED: 'Public Summary Page Scrolled',
    FEEDBACK_CLICKED: 'Summary Feedback Clicked',
    FEEDBACK_SUBMITTED: 'Summary Feedback Submitted',
    TEMPLATE_DROPDOWN_OPENED: 'Summary Template Dropdown Opened',
    TEMPLATE_APPLIED: 'Summary Template Applied',
    TEMPLATE_APPLY_SUCCEEDED: 'Summary Template Apply Succeeded',
    TEMPLATE_APPLY_FAILED: 'Summary Template Apply Failed',
    TEMPLATE_DEFAULT_RESTORED: 'Summary Template Default Restored',
    TEMPLATE_MANAGE_LINK_CLICKED: 'Summary Template Manage Link Clicked',
  },
  SUMMARY_TEMPLATES: {
    PAGE_VIEWED: 'Summary Templates Page Viewed',
    CREATE_CLICKED: 'Summary Templates Create Clicked',
    WORKSPACE_CREATED: 'Workspace Summary Template Created',
    WORKSPACE_UPDATED: 'Workspace Summary Template Updated',
    WORKSPACE_DELETED: 'Workspace Summary Template Deleted',
    VALIDATE_STARTED: 'Workspace Summary Template Validate Started',
    VALIDATE_COMPLETED: 'Workspace Summary Template Validate Completed',
    VALIDATE_FAILED: 'Workspace Summary Template Validate Failed',
    SECURITY_RISK_SHOWN: 'Workspace Summary Template Security Risk Shown',
    CORRECTION_CONFIRMED: 'Workspace Summary Template Correction Confirmed',
    CORRECTION_DISCARDED: 'Workspace Summary Template Correction Discarded',
    CURATED_PREVIEW_OPENED: 'Curated Summary Template Preview Opened',
    CURATED_DUPLICATED: 'Curated Summary Template Duplicated',
    EXPLORE_LINK_CLICKED: 'Summary Templates Explore Link Clicked',
  },
  UNANSWERED_SETTINGS: {
    SETTINGS_DISPLAYED: 'Unanswered Calls settings displayed',
    OPTION_CHANGED: 'Unanswered Calls settings option changed',
    SAVED: 'Unanswered Calls settings saved',
    VOICEMAIL_MESSAGE_UPDATED: 'Unanswered Calls voicemail message updated',
    TRANSFER_NUMBER_UPDATED: 'Unanswered Calls call transfer number updated',
    AI_VOICE_CHANGED: 'Unanswered Calls AI receptionist voice changed',
    AI_TONE_CHANGED: 'Unanswered Calls AI receptionist tone changed',
    AI_ANSWER_TYPE_CHANGED: 'Unanswered Calls AI receptionist answer type changed',
    AI_LANGUAGE_CHANGED: 'Unanswered Calls AI receptionist language changed',
    AI_CUSTOM_PROMPT_UPDATED: 'Unanswered Calls AI receptionist custom prompt updated',
    AI_GREETING_UPDATED: 'Unanswered Calls AI receptionist greeting updated',
    AI_CLOSING_UPDATED: 'Unanswered Calls AI receptionist closing updated',
    AI_KNOWLEDGE_UPDATED: 'Unanswered Calls AI receptionist knowledge updated',
    AI_WEBSITE_ADDED: 'Unanswered Calls AI receptionist website added',
    AI_WEBSITE_REMOVED: 'Unanswered Calls AI receptionist website removed',
    AI_TEST_CALL: 'Unanswered Calls AI receptionist test call',
    AI_TAB_CHANGED: 'Unanswered Calls AI receptionist tab changed',
    AI_TRANSFER_RULES_UPDATED: 'Unanswered Calls AI receptionist transfer rules updated',
  },
  CALL: {
    /**
     * Unified outbound-call funnel. Use `entry_point` to break down by
     * source: 'sidebar_dialer' | 'call_bar' | 'phone_link' | 'command_center'
     * | 'shortcut' | 'deep_link' | 'power_dialer' | 'contact_panel'
     * | 'cti_dialer'.
     * Supersedes `CALL_IDLE.CALL_INITIATED` (call-bar-only).
     */
    STARTED: 'Call Started',
    ACTIVE_CONTEXT_BUTTON_TAPPED: 'Dialer Active Context Button Tapped',
    ENDED: 'Call Ended',
    MUTED: 'Call Muted',
    UNMUTED: 'Call Unmuted',
    EARMUFF_ENABLED: 'Call Earmuff Enabled',
    EARMUFF_DISABLED: 'Call Earmuff Disabled',
    NOISE_SUPPRESSION_ENABLED: 'Call Noise Suppression Enabled',
    NOISE_SUPPRESSION_DISABLED: 'Call Noise Suppression Disabled',
    DIALPAD_OPENED: 'Call Dialpad Opened',
    PIP_OPENED: 'Call PiP Opened',
    PIP_CLOSED: 'Call PiP Closed',
    CONTACT_CREATE_CLICKED: 'Call Contact Create Clicked',
    TRANSFER_MENU_OPENED: 'Warm Transfer Menu Opened',
    TRANSFER_MENU_CLOSED: 'Warm Transfer Menu Closed',
    TRANSFER_STARTED: 'Warm Transfer Started',
    TRANSFER_SUCCEEDED: 'Warm Transfer Succeeded',
    TRANSFER_FAILED: 'Warm Transfer Failed',
  },
  CALL_TAGS: {
    SETTINGS_TAPPED: 'Call Tags Settings Tapped',
    SETTINGS_DISPLAYED: 'Call Tags Settings Displayed',
    DELETED: 'Call Tag Deleted',
    CREATE_ACTION_MODAL_DISPLAYED: 'Create Tag Action Modal Displayed',
    CREATE_MODAL_DISPLAYED: 'Create Tag Modal Displayed',
    LIBRARY_DISPLAYED: 'Call Tags Library Displayed',
    EDIT_TAPPED: 'Call Tag Edit Tapped',
    EDIT_MODAL_DISPLAYED: 'Call Tag Edit Modal Displayed',
    EDITED: 'Call Tag Edited',
  },
  CALL_QUALITY_FEEDBACK: {
    OPENED: 'Call Quality Feedback Opened',
    DISMISSED: 'Call Quality Feedback Dismissed',
    SUBMITTED: 'Call Quality Feedback Submitted',
  },
  CALL_ENDED_TOAST: {
    SHOWN: 'Call Ended Toast Shown',
    CONVERSATION_OPENED: 'Call Ended Toast Conversation Opened',
    FEEDBACK_OPENED: 'Call Ended Toast Feedback Opened',
    DISMISSED: 'Call Ended Toast Dismissed',
    AUTO_DISMISSED: 'Call Ended Toast Auto Dismissed',
  },
  CALL_PIP: {
    DECLINED: 'PiP Call Declined',
    ACCEPTED: 'PiP Call Accepted',
    HUNG_UP: 'PiP Call Hung Up',
    MUTED: 'PiP Call Muted',
    UNMUTED: 'PiP Call Unmuted',
  },
  CALL_IDLE: {
    /**
     * @deprecated No longer emitted. Use `CALL.STARTED` with
     * `entry_point: 'call_bar'`. Constant kept so legacy chart references
     * resolve until they are migrated; safe to remove afterwards.
     */
    CALL_INITIATED: 'Call Bar Call Initiated',
    SMS_INITIATED: 'Call Bar SMS Initiated',
    LINE_CHANGED: 'Call Bar Line Changed',
  },
  REPLACE_NUMBER: {
    STARTED: 'Replace Number Started',
    CONFIRMED: 'Replace Number Confirmed',
    NUMBER_SELECTED: 'Replace Number Number Selected',
    SUCCEEDED: 'Replace Number Succeeded',
    FAILED: 'Replace Number Failed',
  },
  ANALYTICS_OVERVIEW: {
    CHART_METRIC_CHANGED: 'Analytics Overview Chart Metric Changed',
    TABLE_SORTED: 'Analytics Overview Table Sorted',
    SDR_HOVERED: 'Analytics Overview SDR Hovered',
    DATE_RANGE_CHANGED: 'Analytics Overview Date Range Changed',
    CSV_EXPORTED: 'Analytics Overview CSV Exported',
  },
  FAVORITES: {
    CLICKED: 'Contact Favorites Clicked',
  },
  TRANSFER_OWNERSHIP: {
    TAPPED: 'Transfer Ownership Tapped',
    MODAL_DISPLAYED: 'Transfer Ownership Confirmation Modal Displayed',
    CONFIRMED: 'Transfer Ownership Confirmed',
    SUCCEEDED: 'Transfer Ownership Succeeded',
    FAILED: 'Transfer Ownership Failed',
  },
  TEAM_MEMBER_REMOVE: {
    CLICKED: 'Team Member Remove Clicked',
    SUCCEEDED: 'Team Member Remove Succeeded',
    FAILED: 'Team Member Remove Failed',
  },
  TRIAL_END_MODAL: {
    DISPLAYED: 'Trial End Modal Displayed',
    CONFIRMED: 'Trial End Modal Confirmed',
  },
  NUMBER_SETTINGS_HEADER: {
    NUMBER_SWITCHED: 'Number Settings Number Switched',
  },
  NUMBER_SETTINGS_GENERAL: {
    LINE_NAME_SAVED: 'Number Settings Line Name Saved',
    AI_VOICE_CHANGED: 'Number Settings AI Voice Changed',
    // Preserves the legacy event name emitted by the old settings page so the
    // Amplitude funnel stays continuous across the redesign.
    BUSY_MODE_TOGGLED: 'Busy Mode Toggled',
    INTERNATIONAL_CALLING_TOGGLED: 'International Calling Toggled',
    SUMMARY_TEMPLATE_CHANGED: 'Number Settings Summary Template Changed',
  },
  USER_PROMPTS: {
    SAVED: 'User Prompts Saved',
  },
  NUMBER_SETTINGS_NOTIFICATIONS: {
    // Preserves the legacy event name from the old settings page.
    EMAIL_TOGGLED: 'Email Notifications Toggled',
  },
  NUMBER_SETTINGS_USERS: {
    ADDED: 'Number Settings Users Added',
    REMOVED: 'Number Settings Users Removed',
  },
  NUMBER_SETTINGS_DELETE: {
    SUCCEEDED: 'Number Settings Number Deleted',
  },
  NUMBER_SETTINGS_IVR: {
    SECTION_VIEWED: 'Number Settings IVR Section Viewed',
    ENABLED: 'Number Settings IVR Enabled',
    DISABLED: 'Number Settings IVR Disabled',
    OPTION_ADDED: 'Number Settings IVR Option Added',
    OPTION_REMOVED: 'Number Settings IVR Option Removed',
    CONTENT_SAVED: 'Number Settings IVR Content Saved',
  },
  NUMBER_SETTINGS_CALL_ROUTING: {
    MODE_TOGGLED: 'Number Settings Call Routing Mode Toggled',
    RING_TIME_CHANGED: 'Number Settings Call Routing Ring Time Changed',
    CASCADE_MEMBER_TOGGLED: 'Number Settings Call Routing Cascade Member Toggled',
    CASCADE_ORDER_CHANGED: 'Number Settings Call Routing Cascade Order Changed',
    SIMULTANEOUS_MEMBER_TOGGLED: 'Number Settings Call Routing Simultaneous Member Toggled',
  },
  NUMBER_SETTINGS_MISSED_CALL: {
    OPTION_CHANGED: 'Number Settings Missed Call Option Changed',
    OPTION_SAVED: 'Number Settings Missed Call Option Saved',
  },
  NUMBER_SETTINGS_BUSINESS_HOURS: {
    // Preserves the legacy event name (and PascalCase `Status` payload key,
    // see number-settings-business-hours-section.tsx) from the old business
    // hours page so the existing Amplitude dashboard keeps working.
    STATUS_CHANGED: 'Settings Phone Number Business Hours Status Changed',
    SCHEDULE_CHANGED: 'Number Settings Business Hours Schedule Changed',
    TIMEZONE_CHANGED: 'Number Settings Business Hours Timezone Changed',
    WHEN_CLOSED_OPTION_CHANGED: 'Number Settings Business Hours When Closed Option Changed',
    WHEN_CLOSED_OPTION_SAVED: 'Number Settings Business Hours When Closed Option Saved',
  },
  BUSY_MODE: {
    ENABLED: 'Busy Mode Enabled',
    DISABLED: 'Busy Mode Disabled',
    BANNER_VIEWED: 'Busy Mode Banner Viewed',
    BANNER_CLICKED: 'Busy Mode Banner Clicked',
  },
  MCP_PROMO_BANNER: {
    VIEWED: 'MCP Promo Banner Viewed',
    CLICKED: 'MCP Promo Banner Clicked',
    DISMISSED: 'MCP Promo Banner Dismissed',
  },
  PORT_NUMBER: {
    STARTED: 'Porting Orders Create Started',
    CREATE_SUBMITTED: 'Porting Orders Create Submitted',
    CREATED: 'Porting Orders Created',
    CREATE_FAILED: 'Porting Orders Create Failed',
    STEP_COMPLETED: 'Porting Order Step Completed',
    STEP_FAILED: 'Porting Order Step Failed',
    SUBMITTED: 'Porting Order Submitted',
    AUTOSAVE_FAILED: 'Porting Order Autosave Failed',
    PHONE_NUMBER_AUTOSAVE_FAILED: 'Porting Order Phone Number Autosave Failed',
    PHONE_NUMBER_EDIT_AUTOSAVE_FAILED: 'Porting Order Phone Number Edit Autosave Failed',
  },
  BILLING_CREDITS: {
    TOP_UP_SUBMITTED: 'Credits Top-Up Submitted',
    TOP_UP_CONFIRMED: 'Credits Top-Up Confirmed',
    TOP_UP_SUCCEEDED: 'Credits Top-Up Succeeded',
    TOP_UP_FAILED: 'Credits Top-Up Failed',
    TOP_UP_PAYMENT_VERIFICATION_REQUIRED: 'Credits Top-Up Payment Verification Required',
    TOP_UP_PAYMENT_METHOD_ADDED: 'Credits Top-Up Payment Method Added',
    TOP_UP_PAYMENT_METHOD_FAILED: 'Credits Top-Up Payment Method Failed',
    TOP_UP_VERIFICATION_SUCCEEDED: 'Credits Top-Up Verification Succeeded',
    TOP_UP_VERIFICATION_FAILED: 'Credits Top-Up Verification Failed',
    AUTO_TOP_UP_SETTINGS_UPDATED: 'Auto Top-Up Settings Updated',
  },
  POWER_DIALER: {
    TAB_OPENED: 'Power Dialer Tab Opened',
    EMPTY_STATE_VIEWED: 'Power Dialer Empty State Viewed',
    IMPORT_API_CLICKED: 'Power Dialer Import API Clicked',
    CONNECT_CRM_CLICKED: 'Power Dialer Connect CRM Clicked',
    START_FROM_ALLO_CONTACTS_CLICKED: 'Power Dialer Start From Allô Contacts Clicked',
    API_KEY_CREATED: 'Power Dialer API Key Created',
    SESSION_START_REQUESTED: 'Power Dialer Session Start Requested',
    SESSION_COUNTDOWN_CANCELLED: 'Power Dialer Session Countdown Cancelled',
    SESSION_STARTED: 'Power Dialer Session Started',
    SESSION_ENDED: 'Power Dialer Session Ended',
    CONTACT_SKIPPED: 'Power Dialer Contact Skipped',
    CONTACT_REMOVED: 'Power Dialer Contact Removed',
    QUEUE_CLEARED: 'Power Dialer Queue Cleared',
    VIEW_MODE_CHANGED: 'Power Dialer View Mode Changed',
    SESSION_SUMMARY_VIEWED: 'Power Dialer Session Summary Viewed',
    SESSION_SUMMARY_DISMISSED: 'Power Dialer Session Summary Dismissed',
    SESSION_SUMMARY_SHARED: 'Power Dialer Session Summary Shared',
    MIDDLE_LAYOUT_ARRANGED: 'Power Dialer Middle Layout Arranged',
  },
  INTEGRATIONS: {
    REQUEST_CLICKED: 'Integration Request Clicked',
    REQUEST_SUBMITTED: 'Integration Request Submitted',
  },
  PROFILE: {
    NOTIFICATIONS_EMAIL_UPDATED: 'Notifications Email Updated',
  },
  /**
   * Top-of-funnel signal for the Actions tab on the summary detail view.
   * Fires once per mount when the active tab settles on `'actions'`.
   *
   * Lives top-level (not nested under SUMMARIES, EMAIL, SMS, CONTACT, or
   * CALENDAR) because the funnel is shared across all four post-call
   * action surfaces — the team queries it as one entry point, then
   * breaks down by `entry_point` and per-card `CARD_EXPANDED` events.
   */
  POST_CALL_ACTIONS_TAB: {
    VIEWED: 'Post-Call Actions Tab Viewed',
  },
  POST_CALL_EMAIL: {
    CARD_EXPANDED: 'Post-Call Email Card Expanded',
    GENERATION_REQUESTED: 'Post-Call Email Generation Requested',
    GENERATION_SUCCEEDED: 'Post-Call Email Generation Succeeded',
    GENERATION_FAILED: 'Post-Call Email Generation Failed',
    FIELD_EDITED: 'Post-Call Email Field Edited',
    SEND_CLICKED: 'Post-Call Email Send Clicked',
  },
  POST_CALL_SMS: {
    CARD_EXPANDED: 'Post-Call SMS Card Expanded',
    GENERATION_REQUESTED: 'Post-Call SMS Generation Requested',
    GENERATION_SUCCEEDED: 'Post-Call SMS Generation Succeeded',
    GENERATION_FAILED: 'Post-Call SMS Generation Failed',
    BODY_EDITED: 'Post-Call SMS Body Edited',
    SEND_CLICKED: 'Post-Call SMS Send Clicked',
    /** Fires when the user actually hits Send in the Allo inbox after
     *  the body was pre-filled from the post-call card. Unlike email,
     *  we control the full delivery path so we can track this. */
    MESSAGE_SENT: 'Post-Call SMS Message Sent',
  },
  POST_CALL_CONTACT: {
    CARD_EXPANDED: 'Post-Call Contact Card Expanded',
    FIELD_EDITED: 'Post-Call Contact Field Edited',
    CONFIRM_CLICKED: 'Post-Call Contact Confirm Clicked',
    /** Single event covering both success and failure of the
     *  PATCH/POST mutation. Discriminated by `status`. Unlike email/SMS
     *  generation (long async LLM calls split into SUCCEEDED/FAILED), a
     *  contact mutation is a short binary API call — one event with a
     *  status field is the cleaner shape. */
    MUTATION_COMPLETED: 'Post-Call Contact Mutation Completed',
  },
  POST_CALL_CALENDAR: {
    CARD_EXPANDED: 'Post-Call Calendar Card Expanded',
    FIELD_EDITED: 'Post-Call Calendar Field Edited',
    /** Terminal action — no MUTATION_COMPLETED because we hand off to
     *  an external provider (Google / Outlook tab, .ics download) and
     *  cannot observe whether the user actually saved the event. */
    PROVIDER_CLICKED: 'Post-Call Calendar Provider Clicked',
  },
  /**
   * Sidebar-revamp navigation analytics. Each event is emitted directly
   * at the trigger site (sidebar click handler / keyboard shortcut /
   * dropdown selection). Programmatic navigations (redirects, browser
   * back/forward) are intentionally NOT tracked — they don't represent
   * user intent.
   *
   * `*.TAB_OPENED` events share a property:
   *   entry_point: 'sidebar_tab' | 'shortcut'
   */
  CALLS: {
    TAB_OPENED: 'Calls Tab Opened',
  },
  /** Replaces legacy hardcoded `'Open Contacts'`. */
  CONTACTS: {
    TAB_OPENED: 'Contacts Tab Opened',
  },
  /**
   * Distinct from `ANALYTICS_OVERVIEW.*` (in-page interactions).
   * Value preserves the legacy `'Team Tab Opened'` string previously
   * emitted from `/_app/analytics.tsx`'s onEnter so existing dashboards
   * keep working.
   */
  ANALYTICS: {
    TAB_OPENED: 'Team Tab Opened',
  },
  INBOX_SWITCHER: {
    /**
     * Fires only on actual inbox change, not on dropdown open.
     * Properties: total_inbox_count.
     */
    SWITCHED: 'Inbox Switched',
  },
  SETTINGS: {
    /**
     * Answers "by which door do users enter Settings?".
     * Property `entry_point`: 'sidebar_tab' | 'inbox_switcher_dropdown'
     * | 'footer_workspace_menu' | 'profile_dropdown' | 'shortcut'.
     * Absorbs the legacy `'Open Settings'` and `'Workspace Modal Tapped'`
     * `{ choice: 'settings' }` emits from the revamp surfaces.
     */
    OPENED: 'Settings Opened',
  },
  STATUS_PAGE: {
    /** Property `severity`: 'outage' | 'degraded' | 'maintenance'. */
    BANNER_VIEWED: 'Status Page Banner Viewed',
    BANNER_OPENED: 'Status Page Banner Opened',
    SPLASH_LINK_OPENED: 'Status Page Splash Link Opened',
  },
} as const;
